import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useNavigate } from "react-router";
import DueOnListItems from "app/accountTransfer/utilities/list/DueOnListItems";
import { useBpayTransferRequestMutation, useRequestPayAnyoneTransferMutation, PartyLimitDocument, useRequestInterInstrumentTransferMutationMutation, } from "generated/generated-models";
import { createTransferTimes, transferTypes, } from "app/accountTransfer/utilities/fundsTransfers";
import { useTransferPayments } from "./context/useTransferPayments";
import { MoneyFormatted } from "app/components/MoneyFormatted";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { Alert, LoadingButton } from "@mui/lab";
import { toDateOnly } from "core/util/dateExtensions";
import { Dialog, DialogTitle, Typography, DialogContent, List, ListItem, ListItemText, Button, useMediaQuery, Box, } from "@mui/material";
import { useSafeArea } from "app/layout/Navbar/useSafeArea";
const DetermineTransferType = (fromAccount, toAccount) => {
    if (toAccount?.IsBPay) {
        return "BPay";
    }
    else if (!fromAccount?.IsBankAccount && !toAccount?.IsBankAccount) {
        if (fromAccount.LinkedInstrumentsAccounts?.includes(toAccount.AccountId)) {
            return "InterInstrument";
        }
        return "InterAccount";
    }
    else if (!fromAccount?.IsBankAccount &&
        toAccount?.IsBankAccount &&
        toAccount.IsExternal) {
        return "PayAnyone";
    }
    else if (!fromAccount?.IsBankAccount &&
        toAccount?.IsBankAccount &&
        !toAccount.IsExternal) {
        return "Redraw";
    }
    else if (fromAccount?.IsBankAccount && !toAccount?.IsBankAccount) {
        return "Payment";
    }
    else {
        return "Unknown";
    }
};
const SetTransferTypeId = (transferType) => {
    switch (transferType) {
        case "BPay":
            return transferTypes.BpayTransferTypeId;
        case "PayAnyone":
            return transferTypes.PayAnyoneTransferTypeId;
        case "Redraw":
            return transferTypes.RedrawTransferTypeId;
        case "Payment":
            return transferTypes.RepaymentTransferTypeId;
        case "InterInstrument":
            return transferTypes.InterInstrumentTransferTypeId;
        default:
            return transferTypes.InterAccountTransferTypeId;
    }
};
const ConfirmTransfer = () => {
    const navigate = useNavigate();
    const [bpayTransfer, bpayData] = useBpayTransferRequestMutation();
    const [requestPayment, paymentData] = useRequestPayAnyoneTransferMutation();
    const [requestInterInstrumentPayment, interInstrumentPaymentData] = useRequestInterInstrumentTransferMutationMutation();
    const { fromAccount, toAccount, formState, modifyTransfer, confirmTransfer, updateTransferType, updateIsPending, } = useTransferPayments();
    const transferType = DetermineTransferType(fromAccount, toAccount);
    const transferTypeId = SetTransferTypeId(transferType);
    const fullScreen = useMediaQuery("(max-width: 768px)");
    const [, setOpen] = React.useState(false);
    const { insetTop } = useSafeArea();
    const handleClose = () => {
        setOpen(false);
    };
    if (formState == null || formState.amount == null) {
        navigate(".");
    }
    const sendPaymentRequest = () => {
        const transferTimes = createTransferTimes(formState);
        if (transferType === "BPay") {
            bpayTransfer({
                variables: {
                    input: {
                        AccountId: fromAccount?.AccountId,
                        Amount: formState.amount,
                        Billercode: toAccount?.BillerCode,
                        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                        CustomerReferenceNumber: toAccount?.AccountNumber,
                        SaveBillerDetails: false,
                        BillerDescription: toAccount?.AccountName,
                        WithdrawalDescription: formState.transferDescription,
                        StartingOn: toDateOnly(transferTimes.StartingOn),
                        EndingOn: toDateOnly(transferTimes.EndingOn),
                        Frequency: transferTimes.Frequency,
                        NumberOfTimesAmount: transferTimes.NumberOfTimesAmount,
                        RecurrenceEnd: transferTimes.RecurrenceEnd,
                    },
                },
                refetchQueries: [
                    {
                        query: PartyLimitDocument,
                    },
                ],
            }).then((x) => {
                const fundsTransferId = x.data?.bpayTransferRequest?.FundsTransfer?.Id;
                updateTransferType(transferType);
                confirmTransfer(fundsTransferId);
            });
        }
        else if (transferType === "InterInstrument") {
            requestInterInstrumentPayment({
                variables: {
                    input: {
                        FromAccountId: fromAccount?.AccountId,
                        ToAccountId: toAccount?.AccountId,
                        Amount: formState.amount,
                        DepositDescription: formState.transferDescription,
                        WithdrawalDescription: formState.transferReference,
                        StartingOn: toDateOnly(transferTimes.StartingOn),
                        EndingOn: toDateOnly(transferTimes.EndingOn),
                        RecurrenceEnd: transferTimes.RecurrenceEnd,
                        Frequency: transferTimes.Frequency,
                        NumberOfTimesAmount: transferTimes.NumberOfTimesAmount,
                    },
                },
                refetchQueries: [
                    {
                        query: PartyLimitDocument,
                    },
                ],
            }).then((x) => {
                const fundsTransferId = x.data?.requestInterInstrumentTransfer?.FundsTransfer?.Id;
                if (x.data?.requestInterInstrumentTransfer?.Success === true) {
                    const pending = formState.useNPP &&
                        x.data?.requestInterInstrumentTransfer?.Message !==
                            null;
                    updateTransferType(transferType);
                    updateIsPending(pending ?? false);
                    confirmTransfer(fundsTransferId);
                }
            });
        }
        else {
            requestPayment({
                variables: {
                    input: {
                        AccountId: transferType === "Payment"
                            ? toAccount?.AccountId
                            : fromAccount?.AccountId,
                        Amount: formState.amount,
                        BankAccountId: transferType === "Payment"
                            ? fromAccount?.AccountId
                            : toAccount?.AccountId,
                        DepositDescription: formState.transferDescription,
                        WithdrawalDescription: formState.transferReference,
                        StartingOn: toDateOnly(transferTimes.StartingOn),
                        EndingOn: toDateOnly(transferTimes.EndingOn),
                        Frequency: transferTimes.Frequency,
                        TransferType: transferTypeId,
                        FromAccountId: transferType === "InterAccount"
                            ? fromAccount?.AccountId
                            : null,
                        ToAccountId: transferType === "InterAccount"
                            ? toAccount?.AccountId
                            : null,
                        RecurrenceEnd: transferTimes.RecurrenceEnd,
                        NumberOfTimesAmount: transferTimes.NumberOfTimesAmount,
                        FixedAmountOption: true,
                        UseNPP: formState.useNPP,
                    },
                },
                refetchQueries: [
                    {
                        query: PartyLimitDocument,
                    },
                ],
            }).then((x) => {
                const fundsTransferId = x.data?.requestPayAnyoneTransfer?.FundsTransfer?.Id;
                if (x.data?.requestPayAnyoneTransfer?.Success === true) {
                    const pending = formState.useNPP &&
                        x.data?.requestPayAnyoneTransfer?.Message !== null;
                    updateTransferType(transferType);
                    updateIsPending(pending ?? false);
                    confirmTransfer(fundsTransferId);
                }
            });
        }
    };
    return (_jsxs(Dialog, { fullScreen: fullScreen, open: true, className: "confirm-transfer-payment", onClose: handleClose, children: [_jsxs(DialogTitle, { children: [_jsx(Box, { sx: { height: insetTop } }), _jsx(CurrencyExchangeIcon, {}), _jsx(Typography, { variant: "h5", gutterBottom: true, component: "h5", sx: { mx: 2, mb: 2 }, children: "Confirm Transfer" })] }), _jsx(DialogContent, { sx: { m: 4, mt: 0 }, children: _jsxs(DialogContent, { children: [_jsx(Typography, { variant: "body1", gutterBottom: true, component: "p", sx: { textAlign: "center" }, children: "Please ensure all details are correct before proceeding. Once confirmed your transfer is final." }), _jsxs(Typography, { variant: "body1", gutterBottom: true, component: "p", sx: { textAlign: "center" }, className: "transfer-amount", children: [_jsx(MoneyFormatted, { amount: formState.amount }), _jsx("span", { className: "caption", children: "Amount to be paid" })] }), _jsxs(List, { sx: { mb: 2 }, className: "transfer-details", children: [_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemText, { primary: _jsxs(Typography, { children: [_jsx("span", { className: "subtitle", children: "From: " }), fromAccount?.AccountName, " -", " ", fromAccount?.AccountNumber || ""] }) }) }), _jsx(ListItem, { disablePadding: true, children: toAccount?.BillerCode || toAccount?.BSB ? (_jsx(ListItemText, { primary: _jsxs(Typography, { children: [_jsxs("span", { className: "subtitle", children: ["To:", " "] }), toAccount?.AccountName, " ", _jsx("br", {}), " ", toAccount?.BillerCode ??
                                                    toAccount?.BSB, " ", "| ", toAccount?.AccountNumber ?? ""] }) })) : (_jsx(ListItemText, { primary: _jsxs(Typography, { children: [_jsxs("span", { className: "subtitle", children: ["To:", " "] }), toAccount?.AccountName, " -", " ", toAccount?.AccountNumber ?? ""] }) })) }), _jsx(DueOnListItems, { recurrence: formState.recurrenceEnd || "", paymentDate: formState.startingOn, frequency: formState.frequency || "", endDate: formState.endingOn, repeats: formState.numberOfTimesAmount }), _jsx(ListItem, { disablePadding: true, children: _jsx(ListItemText, { primary: _jsxs(Typography, { children: [_jsxs("span", { className: "subtitle", children: ["Description:", " "] }), " ", formState.transferDescription] }) }) }), formState.transferReference && (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemText, { primary: _jsxs(Typography, { children: [_jsxs("span", { className: "subtitle", children: ["Reference:", " "] }), " ", formState.transferReference] }) }) }))] }), ((paymentData.data?.requestPayAnyoneTransfer?.Message &&
                            !paymentData.data?.requestPayAnyoneTransfer?.Success) ||
                            paymentData.error != null ||
                            bpayData.error != null ||
                            interInstrumentPaymentData.error != null) && (_jsxs(Alert, { severity: "error", children: ["Error submitting transfer request.", " ", paymentData.data?.requestPayAnyoneTransfer
                                    ?.Message ??
                                    bpayData.data?.bpayTransferRequest?.Message ??
                                    interInstrumentPaymentData?.data
                                        ?.requestInterInstrumentTransfer?.Message] })), _jsxs("div", { className: "button-row", children: [_jsx(Button, { className: "btn-cancel", id: "cancel", sx: { p: 1.5, mt: 1 }, variant: "contained", onClick: () => modifyTransfer(), children: "Cancel" }), _jsx(LoadingButton, { type: "submit", id: "submit", sx: { p: 1.5, mt: 1 }, onClick: () => sendPaymentRequest(), disabled: paymentData.loading ||
                                        interInstrumentPaymentData.loading, loading: paymentData.loading ||
                                        interInstrumentPaymentData.loading, loadingPosition: "end", variant: "contained", children: "Confirm" })] })] }) })] }));
};
export default ConfirmTransfer;
